/**
 * Keeps Tab inside an open dialog, and gives focus back when it closes.
 *
 * Three things, because a "focus trap" that only does the first is the one
 * people actually complain about:
 *
 * 1. **Tab stays inside.** Tabbing off the last control wraps to the first, and
 *    Shift+Tab off the first wraps to the last. Without this, Tab walks out of
 *    the sheet and into the page behind — which is still there, still
 *    clickable, and completely invisible to someone using a screen reader who
 *    now has no idea where they are.
 * 2. **Escape closes.** Owned here rather than in each dialog, so every one of
 *    them behaves the same and none of them can forget.
 * 3. **Focus comes back.** Whatever was focused when the dialog opened gets
 *    focus again when it closes. Skipping this drops the caret at the top of
 *    the document, so a keyboard user who opens and closes a sheet loses their
 *    place in the list entirely.
 *
 * The tabbable set is read on each keypress rather than cached: sheets add and
 * remove controls while they're open — a validation message, a review row, a
 * disabled Add button becoming enabled — and a list captured at mount would go
 * stale within a second of real use.
 */

import { useEffect, useRef } from "react";

/**
 * What counts as reachable by Tab.
 *
 * `[tabindex="-1"]` is deliberately excluded: it means "focusable by script,
 * not by Tab", and including it would trap people on things they cannot leave
 * in the direction they expect.
 */
const TABBABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Filtered on what can actually be known, rather than on what looks thorough.
 *
 * Measuring each candidate's box would be the obvious test for "is this
 * visible", and it is the wrong one twice over: a visually-hidden file input is
 * 1×1 and *is* a real control the sheet depends on, and jsdom reports every box
 * as zero, so the check would exclude everything in the very place the
 * behaviour is tested.
 *
 * So: the attributes that unambiguously mean "not reachable", plus the two
 * computed properties that do. Anything subtler is guesswork dressed as rigour.
 */
function tabbableWithin(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(TABBABLE)].filter((element) => {
    if (element.hasAttribute("disabled")) return false;
    if (element.getAttribute("aria-hidden") === "true") return false;
    if (element.closest("[hidden]")) return false;

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    return style?.display !== "none" && style?.visibility !== "hidden";
  });
}

export interface FocusTrapOptions {
  /** Called on Escape. The dialog decides what closing means. */
  onEscape?: () => void;
  /**
   * Focused on open. Without one, the first tabbable thing takes it — which is
   * usually the close button, and usually wrong.
   */
  initialFocus?: () => HTMLElement | null;
}

export function useFocusTrap<T extends HTMLElement>({
  onEscape,
  initialFocus,
}: FocusTrapOptions = {}) {
  const containerRef = useRef<T>(null);
  // Read inside the keydown handler rather than in the dependency list, so a
  // fresh closure each render doesn't tear the listener down and rebind it.
  const escape = useRef(onEscape);
  useEffect(() => {
    escape.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Captured before anything is focused, or we'd "restore" to ourselves.
    const opener = document.activeElement as HTMLElement | null;

    const wanted = initialFocus?.() ?? null;
    const first = wanted ?? tabbableWithin(container)[0] ?? container;
    first.focus?.();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        escape.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      const stops = tabbableWithin(container as HTMLElement);
      if (stops.length === 0) {
        // Nothing to move to, so Tab must not escape either.
        event.preventDefault();
        return;
      }

      const edge = event.shiftKey ? stops[0] : stops[stops.length - 1];
      const target = event.shiftKey ? stops[stops.length - 1] : stops[0];
      const active = document.activeElement;

      // Wrap at the edge, and also when focus has somehow ended up outside —
      // which happens when a control the user was on is removed mid-interaction.
      if (active === edge || !container?.contains(active)) {
        event.preventDefault();
        target.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      // Only if focus is still somewhere in here; if the user has deliberately
      // clicked elsewhere by now, yanking them back would be the rude thing.
      if (!opener || !opener.isConnected) return;
      const active = document.activeElement;
      if (active === document.body || container?.contains(active)) opener.focus?.();
    };
    // Runs once per open. The refs above keep the handlers current without
    // rebinding, and rebinding would re-run the initial focus mid-interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return containerRef;
}
